import type { UserProfile } from '../../../types/userProfile';
import { guestOptimizationStorage } from '../../../utils/guestOptimizationStorage';

import {
  PROFILE_INDUSTRY_OPTIONS,
  PROFILE_JOB_SEARCH_STATUS_OPTIONS,
  PROFILE_JOB_TYPE_OPTIONS,
} from '../../onboarding/steps/preferenceFieldOptions';

/**
 * Must match `UploadStep` `UPLOAD_DRAFT_KEY` — do not rename without updating the optimizer.
 */
export const OPTIMIZER_UPLOAD_DRAFT_STORAGE_KEY = 'opt_upload_draft';

function pickLabel<T extends { value: string; label: string }>(
  options: T[],
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  return options.find(o => o.value === value)?.label ?? value;
}

/** Build a plain-text resume the user can refine in the optimizer upload step. */
export function buildResumePlainTextFromUserProfile(
  p: UserProfile,
): string {
  const parts: string[] = [];

  // Header with name and headline
  const name = p.displayName?.trim();
  if (name) {
    parts.push(name.toUpperCase());
  }

  const headline = p.headline?.trim();
  if (headline) {
    parts.push(headline);
  }

  // Location and contact info
  const contactInfo: string[] = [];
  if (p.location?.trim()) contactInfo.push(p.location.trim());
  if (p.linkedinUrl?.trim()) contactInfo.push(p.linkedinUrl.trim());
  if (p.githubUrl?.trim()) contactInfo.push(p.githubUrl.trim());
  if (p.portfolioUrl?.trim()) contactInfo.push(p.portfolioUrl.trim());
  if (contactInfo.length) {
    parts.push(contactInfo.join(' | '));
  }

  // Professional Summary
  const summary = p.professionalSummary?.trim();
  if (summary) {
    parts.push('');
    parts.push('PROFESSIONAL SUMMARY');
    parts.push('─'.repeat(40));
    parts.push(summary);
  }

  // Work Experience
  if (p.experiences?.length > 0) {
    parts.push('');
    parts.push('WORK EXPERIENCE');
    parts.push('─'.repeat(40));
    for (const exp of p.experiences) {
      parts.push('');
      const dateRange = exp.current
        ? `${exp.startDate} – Present`
        : exp.endDate
          ? `${exp.startDate} – ${exp.endDate}`
          : exp.startDate;
      parts.push(`${exp.title.toUpperCase()}`);
      parts.push(`${exp.company}${exp.location ? ` | ${exp.location}` : ''} | ${dateRange}`);
      if (exp.bullets?.length) {
        for (const bullet of exp.bullets) {
          parts.push(`• ${bullet}`);
        }
      }
    }
  }

  // Education
  if (p.education?.length > 0) {
    parts.push('');
    parts.push('EDUCATION');
    parts.push('─'.repeat(40));
    for (const edu of p.education) {
      parts.push('');
      parts.push(`${edu.degree}`);
      parts.push(`${edu.institution}${edu.location ? `, ${edu.location}` : ''} | ${edu.year}`);
      if (edu.gpa) parts.push(`GPA: ${edu.gpa}`);
      if (edu.honors) parts.push(edu.honors);
    }
  }

  // Skills
  if (p.skills?.length) {
    parts.push('');
    parts.push('SKILLS');
    parts.push('─'.repeat(40));
    parts.push(p.skills.join(', '));
  }

  // Projects
  if (p.projects?.length > 0) {
    parts.push('');
    parts.push('PROJECTS');
    parts.push('─'.repeat(40));
    for (const proj of p.projects) {
      parts.push('');
      parts.push(`${proj.name}${proj.url ? ` (${proj.url})` : ''}`);
      if (proj.technologies?.length) {
        parts.push(`Technologies: ${proj.technologies.join(', ')}`);
      }
      parts.push(proj.description);
    }
  }

  // Certifications
  if (p.certifications?.length > 0) {
    parts.push('');
    parts.push('CERTIFICATIONS');
    parts.push('─'.repeat(40));
    for (const cert of p.certifications) {
      const certDate = cert.date ? ` (${cert.date})` : '';
      parts.push(`• ${cert.name} – ${cert.issuer}${certDate}`);
    }
  }

  // Languages
  if (p.languages?.length > 0) {
    parts.push('');
    parts.push('LANGUAGES');
    parts.push('─'.repeat(40));
    parts.push(p.languages.map(l => `${l.name} (${l.proficiency})`).join(', '));
  }

  // Fallback for sparse profiles without structured data
  if (
    (!p.experiences || p.experiences.length === 0) &&
    (!p.education || p.education.length === 0) &&
    !summary
  ) {
    const role = p.currentRole?.trim();
    if (role || p.experienceYears != null) {
      parts.push('');
      parts.push('CURRENT ROLE');
      parts.push('─'.repeat(40));
      if (role) parts.push(`Title: ${role}`);
      if (p.experienceYears != null) parts.push(`Years of experience: ${p.experienceYears}`);
    }

    const industryLbl = pickLabel(PROFILE_INDUSTRY_OPTIONS, p.industry);
    if (industryLbl) {
      parts.push('');
      parts.push('INDUSTRY');
      parts.push('─'.repeat(40));
      parts.push(industryLbl);
    }
  }

  return parts.join('\n').trim();
}

function readSessionUploadDraftResume(): string | null {
  try {
    const raw = sessionStorage.getItem(OPTIMIZER_UPLOAD_DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { resumeText?: string };
    const t = typeof o.resumeText === 'string' ? o.resumeText.trim() : '';
    return t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

function readMultiOptimizeStoredResume(): string | null {
  try {
    const raw = localStorage.getItem('multi-optimize-resume-data');
    if (!raw) return null;
    const o = JSON.parse(raw) as { parsedText?: string; content?: string };
    const t = String(o.parsedText ?? o.content ?? '').trim();
    return t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

function readGuestStoredResume(): string | null {
  try {
    const g = guestOptimizationStorage.get();
    const rd = g?.resumeData as { parsedText?: string; content?: string } | undefined;
    if (!rd) return null;
    const t = String(rd.parsedText ?? rd.content ?? '').trim();
    return t.length > 0 ? t : null;
  } catch {
    return null;
  }
}

/**
 * Longest non-empty resume plain text among profile snapshot, stored raw resume text,
 * optimizer upload draft, multi-job flow localStorage, and guest optimization —
 * so the agent uses a real CV when the GoGrabJob profile card is still sparse.
 */
export function getBestResumePlainTextForAgent(profile: UserProfile | null): string {
  const candidates: string[] = [];

  if (profile) {
    // Prioritize stored raw resume text if available (from parsed upload)
    if (profile.resumeRawText?.trim()) {
      candidates.push(profile.resumeRawText.trim());
    }
    // Also build from structured profile data
    const fromProfile = buildResumePlainTextFromUserProfile(profile);
    if (fromProfile) candidates.push(fromProfile);
  }

  const draft = readSessionUploadDraftResume();
  if (draft) candidates.push(draft);
  const multi = readMultiOptimizeStoredResume();
  if (multi) candidates.push(multi);
  const guest = readGuestStoredResume();
  if (guest) candidates.push(guest);

  if (candidates.length === 0) return '';
  return candidates.reduce((a, b) => (a.length >= b.length ? a : b));
}

/** Prefill the optimizer upload draft (same shape as UploadStep autosave). */
export function prefillOptimizerUploadDraftFromProfile(p: UserProfile): void {
  const resumeText = buildResumePlainTextFromUserProfile(p);
  if (!resumeText) return;
  const fileName = 'GoGrabJob-profile.txt';
  try {
    sessionStorage.setItem(OPTIMIZER_UPLOAD_DRAFT_STORAGE_KEY, JSON.stringify({
      resumeText,
      fileName,
      fileType: 'text/plain',
      fileSize: new Blob([resumeText]).size,
      parsingMetadata: null,
      parsedStructured: null,
      parsedSectionsFromDocument: null,
    }));
  } catch (e) {
    console.warn('[prefillOptimizerUploadDraftFromProfile] sessionStorage failed', e);
  }
}
